import { tokenManager } from "../auth";

const API_BASE_URL = "/api/v1";

export interface UploadDocumentRequest {
  file: Blob;
  filename: string;
}

export interface UpdateDocumentRequest {
  file: Blob;
  filename: string;
}

export interface UploadDocumentResponse {
  document_id: string;
  agent_id?: string;
  status: string;
  message: string;
  word_count: number;
}

export interface ApiError {
  detail: string;
}

export interface ConversationTokenResponse {
  signed_url: string;
  agent_id: string;
  document_id: string;
  document_name: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Make an authenticated request to the API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await tokenManager.getAccessToken();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status}`;
      try {
        const errorData: ApiError = await response.json();
        errorMessage = errorData.detail || errorMessage;
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Make an authenticated file upload request to the API (multipart/form-data)
   */
  private async uploadRequest<T>(
    endpoint: string,
    method: string,
    file: Blob,
    filename: string
  ): Promise<T> {
    const token = await tokenManager.getAccessToken();

    const formData = new FormData();
    formData.append("file", file, filename);

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        // Do NOT set Content-Type — the browser sets it with the boundary
      },
      body: formData,
    });

    if (!response.ok) {
      let errorMessage = `API Error: ${response.status}`;
      try {
        const errorData: ApiError = await response.json();
        errorMessage = errorData.detail || errorMessage;
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Upload document file to the backend (first upload)
   */
  async uploadDocument(request: UploadDocumentRequest): Promise<UploadDocumentResponse> {
    return this.uploadRequest<UploadDocumentResponse>(
      "/legal-agent/document",
      "POST",
      request.file,
      request.filename
    );
  }

  /**
   * Update an existing document
   */
  async updateDocument(documentId: string, request: UpdateDocumentRequest): Promise<UploadDocumentResponse> {
    return this.uploadRequest<UploadDocumentResponse>(
      `/legal-agent/document/${encodeURIComponent(documentId)}`,
      "PUT",
      request.file,
      request.filename
    );
  }

  /**
   * Get legal agent status
   */
  async getStatus(): Promise<{ status: string; user: string; message: string }> {
    return this.request("/legal-agent/status");
  }

  /**
   * Get conversation token for a document's AI agent
   */
  async getConversationToken(documentId: string): Promise<ConversationTokenResponse> {
    return this.request<ConversationTokenResponse>(`/legal-agent/document/${documentId}/conversation-token`);
  }
}

// Singleton instance
export const apiClient = new ApiClient();
